import { EventRegistry } from '../events/event_registry';
import DebugService from 'app/shared/debug.service';
import ImsObjectCollection from '../collections/ims_object_collection.service';
import { StateMachine } from 'app/shared/state_machine/StateMachine';
import { Moment } from 'moment-timezone';
import { Toast } from 'app/shared/toast/toast.service';
import { errorToText } from 'app/shared/errors/generator';

const log = (...args) => DebugService.log('ApiSync', ...args);

interface ApiSyncEvents {
    sendSuccess(sendResult: any): Promise<any>;

    sendFail(): Promise<any>;

    requestSend(): Promise<any>;

    readSuccess(readResult: any): Promise<any>;

    timeForWrite(): Promise<any>;
}

interface ApiSyncActionHandler {
    // It's time to send!
    startSend(): Promise<any>;

    // Accept the read data as the last from API.
    acceptReadData(readResult: any): Promise<any>;

    // Accept the send response data and check if there is cached read data.
    // if so, compare it to the current apiSyncedObject.
    // If the lastUpdated field of the cached object is NEWER than
    // what is in apiSyncedObject, set the apiSyncedObject and update the
    // syncedObject
    sendComplete(sendResult: any): Promise<any>;

    // A sync is currently in progress. Take the read data
    // and cache it for checking after the sync resolves.
    // If there is already data cached, overwrite it!
    cacheReadData(readResult: any): Promise<any>;
}

// A syncable ims object
export interface Syncable<T> {
    id: number;
    imsType: string;
    clone(): T;
    isEqual(other: T): boolean;
    lastUpdated: Moment;
}

const STATE_IDLE = 'idle';
const STATE_SEND_PENDING = 'sendPending';
const STATE_SENDING = 'sending';
const STATE_SENDING_WITH_SEND_PENDING = 'sendingWithSendPending';

const EVENT_REQUEST_SEND = 'requestSend';
const EVENT_TIME_FOR_WRITE = 'timeForWrite';
const EVENT_SEND_SUCCESS = 'sendSuccess';
const EVENT_CANCEL_SEND = 'cancelSend';
const EVENT_SEND_FAIL = 'sendFail';
const EVENT_READ_SUCCESS = 'readSuccess';

/**
 * This is the main logic for syncing.
 */
class ApiSyncFSM implements ApiSyncEvents, ApiSyncActionHandler {
    private _actionHandlers: Set<ApiSyncActionHandler>;
    private _onStateChangeEventRegistry: EventRegistry;

    private _fsm: StateMachine;

    constructor() {
        this._actionHandlers = new Set();
        this._onStateChangeEventRegistry = new EventRegistry();

        this._fsm = new StateMachine(
            [
                {
                    name: STATE_IDLE,
                    onAfterEnterActions: [
                        () => {
                            this._onStateChangeEventRegistry.notifyListeners();
                        },
                    ],
                    onAfterExitActions: [
                        () => {
                            this._onStateChangeEventRegistry.notifyListeners();
                        },
                    ],
                },
                { name: STATE_SEND_PENDING },
                { name: STATE_SENDING },
                { name: STATE_SENDING_WITH_SEND_PENDING },
            ],
            [
                // STATE IDLE
                {
                    eventName: EVENT_REQUEST_SEND,
                    sourceStateName: STATE_IDLE,
                    destStateName: STATE_SEND_PENDING,
                },
                {
                    eventName: EVENT_TIME_FOR_WRITE,
                    sourceStateName: STATE_IDLE,
                },
                {
                    eventName: EVENT_READ_SUCCESS,
                    sourceStateName: STATE_IDLE,
                    beforeActions: [
                        (readResult) => {
                            return this.acceptReadData(readResult);
                        },
                    ],
                },
                {
                    eventName: EVENT_CANCEL_SEND,
                    sourceStateName: STATE_IDLE,
                },
                // STATE SEND PENDING
                {
                    eventName: EVENT_REQUEST_SEND,
                    sourceStateName: STATE_SEND_PENDING,
                },
                {
                    eventName: EVENT_TIME_FOR_WRITE,
                    sourceStateName: STATE_SEND_PENDING,
                    destStateName: STATE_SENDING,
                    beforeActions: [() => this.startSend()],
                },
                {
                    eventName: EVENT_READ_SUCCESS,
                    sourceStateName: STATE_SEND_PENDING,
                    beforeActions: [(readResult) => this.cacheReadData(readResult)],
                },
                {
                    eventName: EVENT_CANCEL_SEND,
                    sourceStateName: STATE_SEND_PENDING,
                    destStateName: STATE_IDLE,
                },
                // STATE SENDING
                {
                    eventName: EVENT_TIME_FOR_WRITE,
                    sourceStateName: STATE_SENDING,
                },
                {
                    eventName: EVENT_REQUEST_SEND,
                    sourceStateName: STATE_SENDING,
                    destStateName: STATE_SENDING_WITH_SEND_PENDING,
                },
                {
                    eventName: EVENT_SEND_FAIL,
                    sourceStateName: STATE_SENDING,
                    destStateName: STATE_SEND_PENDING,
                },
                {
                    eventName: EVENT_SEND_SUCCESS,
                    sourceStateName: STATE_SENDING,
                    destStateName: STATE_IDLE,
                    beforeActions: [(sendResult) => this.sendComplete(sendResult)],
                },
                {
                    eventName: EVENT_READ_SUCCESS,
                    sourceStateName: STATE_SENDING,
                    beforeActions: [(readResult) => this.cacheReadData(readResult)],
                },
                {
                    eventName: EVENT_CANCEL_SEND,
                    sourceStateName: STATE_SENDING,
                    destStateName: STATE_IDLE,
                },
                // STATE SEND WITH SEND PENDING
                {
                    eventName: EVENT_TIME_FOR_WRITE,
                    sourceStateName: STATE_SENDING_WITH_SEND_PENDING,
                },
                {
                    eventName: EVENT_REQUEST_SEND,
                    sourceStateName: STATE_SENDING_WITH_SEND_PENDING,
                },
                {
                    eventName: EVENT_SEND_FAIL,
                    sourceStateName: STATE_SENDING_WITH_SEND_PENDING,
                    destStateName: STATE_SEND_PENDING,
                },
                {
                    eventName: EVENT_SEND_SUCCESS,
                    sourceStateName: STATE_SENDING_WITH_SEND_PENDING,
                    destStateName: STATE_SEND_PENDING,
                },
                {
                    eventName: EVENT_READ_SUCCESS,
                    sourceStateName: STATE_SENDING_WITH_SEND_PENDING,
                    beforeActions: [(readResult) => this.cacheReadData(readResult)],
                },
                {
                    eventName: EVENT_CANCEL_SEND,
                    sourceStateName: STATE_SENDING_WITH_SEND_PENDING,
                    destStateName: STATE_IDLE,
                },
            ],
        );
    }

    // Callback is called with the new state name
    addOnStateChangeCallback(callback: (string) => void): () => void {
        return this._onStateChangeEventRegistry.addEventListener(callback);
    }

    getCurrentState() {
        return this._fsm.currentStateName;
    }

    addActionHandler(handler: ApiSyncActionHandler) {
        this._actionHandlers.add(handler);
    }

    /**
     * A successful send i.e. 200 response
     * @param sendResult returned ims object
     * @returns {Promise<any>}
     */
    sendSuccess(sendResult): Promise<any> {
        return this._fsm.handleEvent(EVENT_SEND_SUCCESS, sendResult);
    }

    /**
     * A send has timed out or failed in another recoverable
     * way. Retry the send on the next send cycle.
     * @returns {Promise<any>}
     */
    sendFail(): Promise<any> {
        return this._fsm.handleEvent(EVENT_SEND_FAIL);
    }

    /**
     * A send has returned a permission denied or some other
     * unrecoverable response. cancel the send. Could also be used for
     * a user cancel event.
     * @returns {Promise<any>}
     */
    cancelSend(): Promise<any> {
        return this._fsm.handleEvent(EVENT_CANCEL_SEND);
    }

    /**
     * The application has requested data be saved to the API
     * @returns {Promise<any>}
     */
    requestSend(): Promise<any> {
        return this._fsm.handleEvent(EVENT_REQUEST_SEND);
    }

    /**
     * An ims object collection (or some other mechanism) has reported
     * an updated syncable ims object has successfully been received
     * @param readResult
     * @returns {Promise<any>}
     */
    readSuccess(readResult: any): Promise<any> {
        return this._fsm.handleEvent(EVENT_READ_SUCCESS, readResult);
    }

    /**
     * Used by the api sync scheduler to kick off a send, if one was
     * pending
     * @returns {Promise<any>}
     */
    timeForWrite(): Promise<any> {
        return this._fsm.handleEvent(EVENT_TIME_FOR_WRITE);
    }

    // Used for action dispatching
    startSend(): Promise<any> {
        const promises = [...this._actionHandlers].map((handler) => handler.startSend());
        return Promise.all(promises);
    }

    // Used for action dispatching
    acceptReadData(readResult: any): Promise<any> {
        const promises = [...this._actionHandlers].map((handler) =>
            handler.acceptReadData(readResult),
        );
        return Promise.all(promises);
    }

    sendComplete(sendResult: any): Promise<any> {
        const promises = [...this._actionHandlers].map((handler) =>
            handler.sendComplete(sendResult),
        );
        return Promise.all(promises);
    }

    // Used for action dispatching
    cacheReadData(readResult: any): Promise<any> {
        const promises = [...this._actionHandlers].map((handler) =>
            handler.cacheReadData(readResult),
        );
        return Promise.all(promises);
    }
}

const DEFAULT_WRITE_SYNC_INTERVAL_MS = 3000;

/**
 * Sets up the sync timers and triggers the 'time to write'
 * event periodically on the fsm.
 * May want to make this object more complicated in the future.
 * - Enable and disable syncing, i.e. no events fire into the StateMachine
 * - Dynamically change interval periods
 * - Immediate reschedule of interval events
 */
class ApiSyncScheduler {
    private _fsm: ApiSyncFSM;
    private _writeSyncIntervalMs: number;
    private _writeProcessId: any;

    constructor(fsm: ApiSyncFSM, writeSyncIntervalMs?: number) {
        this._fsm = fsm;
        if (!writeSyncIntervalMs) {
            writeSyncIntervalMs = DEFAULT_WRITE_SYNC_INTERVAL_MS;
        }
        this._writeSyncIntervalMs = writeSyncIntervalMs;
        this._setupProcesses();
    }

    _setupProcesses() {
        this._writeProcessId = setInterval(() => {
            this._fsm.timeForWrite();
        }, this._writeSyncIntervalMs);
    }

    destroy() {
        clearInterval(this._writeProcessId);
    }
}

/**
 * Wraps the sync state machine. Executes actions requested by the state machine.
 * Provides some nice type safety for calling events and actions
 */
export class ApiSync<T extends Syncable<T>> implements ApiSyncActionHandler {
    private _imsObjCollection: ImsObjectCollection;
    // The sync object used by the rest of the application. The idea
    // is that most of the application will not know or care about this
    // api sync object
    private _syncedObj: SyncedObject<T>;
    // The sync object that holds the last known data
    // from the API
    private _fsm: ApiSyncFSM;
    private _syncScheduler: ApiSyncScheduler;
    private _rmCollectionListener: () => void;
    private _cachedSyncable: T;

    constructor(
        imsObjCollection: ImsObjectCollection,
        syncedObject: SyncedObject<T>,
        syncWrite: boolean,
        apiSyncScheduler?: ApiSyncScheduler,
    ) {
        this._cachedSyncable = null;
        this._imsObjCollection = imsObjCollection;
        this._syncedObj = syncedObject;

        this._fsm = new ApiSyncFSM();

        if (!apiSyncScheduler) {
            apiSyncScheduler = new ApiSyncScheduler(this._fsm);
        }
        this._syncScheduler = apiSyncScheduler;

        this._fsm.addActionHandler(this);

        if (syncWrite) {
            // When the sync obj is updated, pass it onto the
            // api sync obj to see if anything has changed and if
            // an update needs to go to the api
            this._syncedObj.onSave((obj: T) => {
                return this._fsm.requestSend();
            });
        }

        this._rmCollectionListener = this._imsObjCollection.addOnChangedListener(
            (changedObjById) => {
                const id = this._syncedObj.getCurrentObj().id;
                if (id in changedObjById) {
                    this._fsm.readSuccess(changedObjById[id]);
                }
            },
        );

        log('processes are running');
    }

    // This is somewhat of a hack. If we had a hierarchical state machine implemented we could
    // emit events based upon entering the syncing / not syncing super states
    addOnStateChangeCallback(callback) {
        return this._fsm.addOnStateChangeCallback(callback);
    }

    isSyncing(): boolean {
        return isSyncing(this._fsm.getCurrentState());
    }

    startSend(): Promise<any> {
        return this.sendToApi();
    }

    acceptReadData(obj: T): Promise<any> {
        // Update the synced object with data from the API
        return this._syncedObj.update(obj);
    }

    sendComplete(obj: T): Promise<any> {
        // A successful write to the API. We should update our apiSync object
        // to update latest data the browser and API. Note we use 'set' so
        // no change events are emitted by this action.
        // Step 1: update the local sync object
        this._syncedObj.set(obj);

        // Step 2: see if any data read during our sending has a later last_updated
        // field. Last to write wins and becomes the source of truth
        if (this._cachedSyncable) {
            if (
                this._cachedSyncable.lastUpdated.isAfter(
                    this._syncedObj.getCurrentObj().lastUpdated,
                )
            ) {
                const cachedSyncable = this._cachedSyncable;
                this._cachedSyncable = null;
                return this.acceptReadData(cachedSyncable);
            }
        }
        this._cachedSyncable = null;
        return Promise.resolve();
    }

    // A sync is currently in progress. Take the read data
    // and cache it for checking after the sync resolves.
    // If there is already data cached, overwrite it!
    cacheReadData(readData: Syncable<T>) {
        this._cachedSyncable = readData.clone();
        return Promise.resolve();
    }

    sendToApi(): Promise<any> {
        const obj = this._syncedObj.getCurrentObj();

        if (!obj.id) {
            // At this stage this object cannot sync an object that does not already exist
            // in the database
            throw new Error('No ID on object that is to be synced');
        }

        return this._imsObjCollection
            .updateObject(obj)
            .then((resultObj) => {
                // Carry on with normal state machine operations so we can exit
                // send mode or being the next send if a change has occured whiel the
                // previous send was executing.
                return this._fsm.sendSuccess(resultObj);
            })
            .catch((error) => {
                if (error.status === -1) {
                    // Timed out, retry
                    return this._fsm.sendFail();
                } else {
                    new Toast(Toast.ERROR, 'Saving data failed ' + errorToText(error));
                    return this._fsm.cancelSend();
                }
            });
    }

    // Artificially trigger a write. A convenience when testing. Normally this is triggered
    // at an arbitrary time by the scheduler object so triggering a send in this fashion
    // isn't too bad.
    timeForWrite(): Promise<any> {
        return this._fsm.timeForWrite();
    }

    /**
     * Turns off periodic syncing with the api for this dashboard tab.
     * Called when removing the dashboard tab from display
     */
    destroy() {
        // Send the cancel event just in case we are sending
        this._fsm.cancelSend();
        this._syncScheduler.destroy();
        this._rmCollectionListener();
    }
}

export class SyncedObject<T extends Syncable<T>> {
    private _currentObj: T;
    private _onUpdate: EventRegistry;
    private _onSave: EventRegistry;
    public name: string;

    /**
     * @param {Syncable} obj If you need this can be an uninitialised object
     * @param {ImsObjectCollection} collection
     */
    constructor(obj: T) {
        this._currentObj = obj.clone();
        this._onUpdate = new EventRegistry();
        this._onSave = new EventRegistry();
    }

    /**
     * Returns a clone of the contained current object
     * @returns {Syncable}
     */
    getCurrentObj(): T {
        return this._currentObj.clone();
    }

    /**
     * Sets the contained object. Does not emit any events, allowing the user to trigger
     * any events on their own. This is handy for start of lifetime where we want explicit control.
     * @param {T} obj
     */
    set(obj: T): void {
        log('SET synced object');
        this._currentObj = obj.clone();
    }

    /**
     * Update this synced object from the data source. This is normally triggered by data
     * arriving from the API.
     * @param {T} obj
     * @returns {Promise<any>}
     */
    update(obj: T): Promise<any> {
        // Take a copy of the new object regardless.
        const oldObj = this._currentObj;
        this._currentObj = obj.clone();
        if (!oldObj.isEqual(obj)) {
            log('UPDATE sync object change detected');
            return this._onUpdate.notifyListeners(this._currentObj);
        } else {
            log('UPDATE synced object, NO change detected');
            return Promise.resolve();
        }
    }

    onUpdate(callback: (Syncable) => void): () => void {
        log('synced object on update');
        return this._onUpdate.addEventListener(callback);
    }

    /**
     * Request this synced object to be saved to the data source. This is triggered by
     * UI changes.
     * @param {T} obj
     * @returns {Promise<any>}
     */
    save(obj: T): Promise<any> {
        const oldObj = this._currentObj;
        this._currentObj = obj.clone();
        if (!oldObj.isEqual(obj)) {
            log('SAVE sync object change detected');
            return this._onSave.notifyListeners(this._currentObj);
        } else {
            log('SAVE synced object, NO change detected');
            return Promise.resolve();
        }
    }

    onSave(callback: (Syncable) => void): () => void {
        return this._onSave.addEventListener(callback);
    }
}

function isSyncing(stateName: string): boolean {
    return stateName.toLowerCase().includes('send');
}
